(function () {
  'use strict';

  const SIZES = [
    { name: 'S', width: 56, length: 68, chest: 112 },
    { name: 'M', width: 58, length: 70, chest: 116 },
    { name: 'L', width: 60.5, length: 72, chest: 121 },
    { name: 'XL', width: 62, length: 74, chest: 124 }
  ];

  const FITS = {
    fitted: { label: 'Fit duruş', copy: 'Vücuda daha yakın, yine de hareket alanı bırakan bir görünüm.' },
    regular: { label: 'Normal duruş', copy: 'Pandorica’s kalıbının doğal, rahat ve dengeli duruşu.' },
    oversized: { label: 'Oversize duruş', copy: 'Omuzları düşük, gövdede belirgin biçimde bol bir görünüm.' }
  };

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  function baseSizeFromHeightWeight(height, weight) {
    if (weight <= 68) {
      if (height >= 184) return 2;
      if (height >= 177) return 1;
      return 0;
    }
    if (weight <= 78) {
      if (height <= 168 && weight <= 73) return 0;
      if (height >= 184) return 2;
      return 1;
    }
    if (weight <= 90) {
      if (height >= 186 || (height >= 180 && weight >= 84)) return 3;
      return 2;
    }
    return 3;
  }

  function recommendFromHeightWeight(height, weight, fit) {
    let index = baseSizeFromHeightWeight(height, weight);

    if (fit === 'fitted') {
      const safeToSizeDown =
        (index === 1 && weight <= 72 && height < 181) ||
        (index === 2 && weight <= 80 && height < 182) ||
        (index === 3 && weight <= 88 && height < 185);
      if (safeToSizeDown) index -= 1;
    }

    if (fit === 'oversized') {
      const smallFrameAlreadyOversized = index === 0 && height <= 165 && weight <= 58;
      if (!smallFrameAlreadyOversized) index += 1;
    }

    return clamp(index, 0, SIZES.length - 1);
  }

  function recommendFromMeasurements(chest, waist, fit) {
    const bodyLine = Math.max(chest, waist + 2);
    const minimumEase = fit === 'fitted' ? 3 : fit === 'regular' ? 7 : 13;
    const targetEase = fit === 'fitted' ? 5 : fit === 'regular' ? 10 : 17;
    let bestIndex = SIZES.length - 1;
    let bestDistance = Infinity;

    SIZES.forEach((size, index) => {
      const ease = size.chest - bodyLine;
      if (ease >= minimumEase) {
        const distance = Math.abs(ease - targetEase);
        if (distance < bestDistance) {
          bestIndex = index;
          bestDistance = distance;
        }
      }
    });

    return bestIndex;
  }

  function normalizeSize(value) {
    return String(value || '').trim().toUpperCase().replace(/BEDEN|SIZE|\s/g, '');
  }

  class PandoricasSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';
      this.syncTheme();
      this.dialog = this.querySelector('dialog');
      this.isPage = this.dataset.displayMode === 'page';
      this.panels = Array.from(this.querySelectorAll('[data-panel]'));
      this.resultIndex = 0;
      this.lastTrigger = null;

      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('submit', this.onSubmit.bind(this));
      if (this.isPage) {
        this.dialog.setAttribute('open', '');
        this.dialog.setAttribute('aria-modal', 'false');
        this.showPanel('intro');
      } else {
        this.dialog.addEventListener('cancel', () => this.reset(false));
        this.dialog.addEventListener('close', () => {
          document.documentElement.style.overflow = '';
          if (this.lastTrigger) this.lastTrigger.focus();
        });
        this.dialog.addEventListener('click', (event) => {
          if (event.target === this.dialog) this.close();
        });
      }
    }

    syncTheme() {
      const bodyStyle = getComputedStyle(document.body);
      const rootStyle = getComputedStyle(document.documentElement);
      const scheme = this.closest('[class*="color-scheme-"]');
      const schemeStyle = scheme ? getComputedStyle(scheme) : bodyStyle;
      const themeValue = (name) => schemeStyle.getPropertyValue(name).trim()
        || rootStyle.getPropertyValue(name).trim()
        || bodyStyle.getPropertyValue(name).trim();
      const rgbTheme = (name) => {
        const value = themeValue(name);
        return value ? 'rgb(' + value + ')' : '';
      };
      const heading = document.querySelector('main h1, .product__title, h1');
      const primaryButton = document.querySelector(
        'form[action*="/cart/add"] [name="add"], form[action*="/cart/add"] button[type="submit"], .addtocart-button, .product-form__submit'
      );
      const headingStyle = heading ? getComputedStyle(heading) : null;
      const buttonStyle = primaryButton ? getComputedStyle(primaryButton) : null;
      const opaque = (color) => color && color !== 'transparent' && !/rgba\([^)]*,\s*0\s*\)/.test(color);
      const pageBackground = rgbTheme('--background-rgb-color') || (opaque(bodyStyle.backgroundColor)
        ? bodyStyle.backgroundColor
        : opaque(rootStyle.backgroundColor) ? rootStyle.backgroundColor : '#ffffff');

      this.style.setProperty('--pdr-bg', pageBackground);
      this.style.setProperty('--pdr-ink', rgbTheme('--text-body-rgb-color') || bodyStyle.color || '#2d2644');
      this.style.setProperty('--pdr-heading', rgbTheme('--text-heading-rgb-color') || bodyStyle.color || '#2a2042');
      if (rgbTheme('--foreground-rgb-color')) {
        this.style.setProperty('--pdr-soft', rgbTheme('--foreground-rgb-color'));
        this.style.setProperty('--pdr-soft-strong', rgbTheme('--foreground-rgb-color'));
      }
      if (rgbTheme('--input-bg-rgb-color')) this.style.setProperty('--pdr-input-bg', rgbTheme('--input-bg-rgb-color'));
      this.style.setProperty('--pdr-body-font', bodyStyle.fontFamily || 'Arial, sans-serif');
      this.style.setProperty('--pdr-heading-font', themeValue('--font-family-heading') || (headingStyle && headingStyle.fontFamily) || bodyStyle.fontFamily);

      if (buttonStyle) {
        const accent = opaque(buttonStyle.backgroundColor) ? buttonStyle.backgroundColor : bodyStyle.color;
        this.style.setProperty('--pdr-accent', accent);
        this.style.setProperty('--pdr-accent-ink', buttonStyle.color || pageBackground);
        this.style.setProperty('--pdr-radius', themeValue('--variant-radius') || buttonStyle.borderRadius || '.375rem');
      }
    }

    onClick(event) {
      const action = event.target.closest('[data-action]');
      if (!action) return;

      const name = action.dataset.action;
      if (name === 'open') this.open(action);
      if (name === 'close') this.close();
      if (name === 'choose-method') this.showPanel(action.dataset.target);
      if (name === 'back') this.showPanel('intro');
      if (name === 'restart') this.reset(true);
      if (name === 'use-size') this.applySize();
    }

    onSubmit(event) {
      const form = event.target.closest('[data-finder-form]');
      if (!form) return;
      event.preventDefault();

      const mode = form.dataset.mode;
      const fields = mode === 'height-weight'
        ? [
            { input: form.elements.height, min: 140, max: 210, message: '140–210 cm arasında girin.' },
            { input: form.elements.weight, min: 35, max: 150, message: '35–150 kg arasında girin.' }
          ]
        : [
            { input: form.elements.chest, min: 70, max: 140, message: '70–140 cm arasında girin.' },
            { input: form.elements.waist, min: 55, max: 140, message: '55–140 cm arasında girin.' }
          ];

      if (!this.validate(fields)) return;

      const fit = new FormData(form).get('fit');
      if (mode === 'height-weight') {
        this.resultIndex = recommendFromHeightWeight(Number(form.elements.height.value), Number(form.elements.weight.value), fit);
      } else {
        this.resultIndex = recommendFromMeasurements(Number(form.elements.chest.value), Number(form.elements.waist.value), fit);
      }

      this.renderResult(this.resultIndex, fit, mode);
      this.showPanel('result');
    }

    validate(fields) {
      let valid = true;
      fields.forEach(({ input, min, max, message }) => {
        const value = Number(input.value);
        const error = input.closest('.pdr-size-finder__field').querySelector('[data-error]');
        const invalid = !input.value || Number.isNaN(value) || value < min || value > max;
        input.setAttribute('aria-invalid', String(invalid));
        error.textContent = invalid ? message : '';
        if (invalid && valid) input.focus();
        valid = valid && !invalid;
      });
      return valid;
    }

    renderResult(index, fit, mode) {
      const size = SIZES[index];
      const fitInfo = FITS[fit] || FITS.regular;
      const isBoundary = (index === 0 && fit === 'fitted') || (index === SIZES.length - 1 && fit === 'oversized');
      const resultSize = this.querySelector('[data-result-size]');
      const useButton = this.querySelector('[data-use-size]');

      resultSize.textContent = size.name;
      this.querySelector('[data-result-fit]').textContent = fitInfo.label;
      this.querySelector('[data-result-heading]').textContent = size.name + ' beden seni bekliyor.';
      this.querySelector('[data-result-copy]').textContent = isBoundary
        ? fitInfo.copy + ' Bu sonuç mevcut beden aralığının sınırında; daha kesin seçim için ürün ölçülerini sevdiğin bir tişörtle karşılaştır.'
        : fitInfo.copy + ' Öneri, Pandorica’s geniş kalıbı ve gerçek müşteri beden yanıtlarıyla eşleştirildi.';
      this.querySelector('[data-result-width]').textContent = String(size.width).replace('.', ',') + ' cm';
      this.querySelector('[data-result-length]').textContent = size.length + ' cm';
      this.querySelector('[data-confidence]').innerHTML = mode === 'measurements'
        ? '<span>● Ölçüye dayalı sonuç</span> — En güvenilir seçim yolu.'
        : '<span>● Boy–kilo tahmini</span> — Göğüs ve bel ölçüsü sonucu hassaslaştırır.';
      useButton.textContent = size.name + ' BEDENİ SEÇ';
      useButton.dataset.size = size.name;
    }

    showPanel(name) {
      this.panels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.panel === name));
      const content = this.querySelector('.pdr-size-finder__content');
      if (content) content.scrollTop = 0;
      const active = this.querySelector('[data-panel].is-active');
      const focusTarget = active && active.querySelector('input, button');
      if (focusTarget) window.setTimeout(() => focusTarget.focus(), 30);
    }

    open(trigger) {
      if (this.isPage) return;
      this.lastTrigger = trigger;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
      this.showPanel('intro');
    }

    close() {
      if (this.isPage) return;
      if (this.dialog.open && typeof this.dialog.close === 'function') this.dialog.close();
      else this.dialog.removeAttribute('open');
      document.documentElement.style.overflow = '';
      if (!this.dialog.open && this.lastTrigger) this.lastTrigger.focus();
    }

    reset(showIntro) {
      this.querySelectorAll('[data-finder-form]').forEach((form) => {
        form.reset();
        form.querySelectorAll('[aria-invalid]').forEach((input) => input.setAttribute('aria-invalid', 'false'));
        form.querySelectorAll('[data-error]').forEach((error) => { error.textContent = ''; });
      });
      if (showIntro) this.showPanel('intro');
    }

    applySize() {
      const size = SIZES[this.resultIndex].name;
      const linkedForm = this.dataset.productFormId ? document.getElementById(this.dataset.productFormId) : null;
      const productArea = (linkedForm && linkedForm.closest('.featured-product-detail, .product-detail, .main-product, product-info, product-component'))
        || this.closest('.featured-product-detail, .product-detail, .main-product, product-info, product-component')
        || document;
      let selected = false;

      {
        const selects = Array.from(productArea.querySelectorAll('select'));
        for (const select of selects) {
          const option = Array.from(select.options).find((item) => normalizeSize(item.textContent) === size || normalizeSize(item.value) === size);
          if (option) {
            select.value = option.value;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            selected = true;
            break;
          }
        }

        if (!selected) {
          const radios = Array.from(productArea.querySelectorAll('.variant-option-input, .variant-dropdown__item input, input[type="radio"]'));
          const radio = radios.find((item) => {
            const label = item.id ? document.querySelector('label[for="' + CSS.escape(item.id) + '"]') : null;
            return normalizeSize(item.value) === size || (label && normalizeSize(label.textContent) === size);
          });
          if (radio) {
            radio.click();
            selected = true;
          }
        }

        if (!selected) {
          const candidates = Array.from(productArea.querySelectorAll('button, [role="radio"], [data-option-value], .variant-option-label'));
          const candidate = candidates.find((item) => normalizeSize(item.dataset.optionValue || item.textContent) === size);
          if (candidate) {
            candidate.click();
            selected = true;
          }
        }
      }

      this.close();
      this.showToast(selected ? size + ' beden ürün seçeneklerinde işaretlendi.' : 'Önerin: ' + size + ' beden.');
    }

    showToast(message) {
      const toast = this.querySelector('[data-toast]');
      toast.textContent = message;
      toast.classList.add('is-visible');
      window.clearTimeout(this.toastTimer);
      this.toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
    }
  }

  if (!customElements.get('pandoricas-size-finder')) {
    customElements.define('pandoricas-size-finder', PandoricasSizeFinder);
  }
})();
